import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';

import { closeWidget, setVisualViewportOffset } from 'actions/main';

import {
  HandoffStatus,
  selectConversationId,
  selectIsMobileSDK,
  selectIsProactiveChatVisible,
  setForethoughtLiveChatStepSent,
  setWindowUpdatedContextVariables,
  showProactivePrompt,
} from 'reducers/workflows';
import {
  handleClickOutsideIframe,
  sendConversationAlert,
  updateHelpdeskConversation,
} from 'actions/workflows-v2/workflowsV2';
import {
  cleanConversation,
  configWidget,
  sendTrackingEvent,
  setDidUserCloseProactiveChat,
  setTriggerEventPayloadContextVariables,
  setTriggerEventPayloadEventName,
} from 'actions/workflows/workflows';
import { isEventTrusted, postMessage } from '../../utils/postMessage';
import { sendResizeiframeMessage } from './utils';
import { useHandleWorkflowTriggerOnClick } from './hooks';
import { disablePersistence } from 'persistence/persistenceBetweenTabs';
import {
  setIntentFiltersForPreview,
  setPreviewIsLiveMode,
} from 'actions/config/config';
import { postIframeResizeEvent } from 'utils/postIframeResizeEvent';
import { setHandoffStatusAndErrorMessage } from 'reducers/workflows/index';
import { scrollToTheBottom } from 'utils/helpers';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';

export const usePostMessageListener = (): void => {
  const dispatch = useAppDispatch();
  const handleWorkflowTriggerOnClick = useHandleWorkflowTriggerOnClick();
  const isProactiveChatVisible = useAppSelector(selectIsProactiveChatVisible);
  const isMobileSDK = useAppSelector(selectIsMobileSDK);
  const conversationId = useAppSelector(selectConversationId);

  const handoffSolveCallback = useCallback(
    (success: boolean, hideWidget = true) => {
      if (success && hideWidget) {
        if (isMobileSDK) {
          // Don't close on mobile. If we close it, the next time it opens it shows the button instead of the
          // widget, but do send the resize event so iOS closes the modal
          sendResizeiframeMessage(false);
        } else {
          postMessage({ event: 'hideSolveWidget' });
        }
      }
    },
    [isMobileSDK],
  );

  const handoffWorkflowsCallback = useCallback(
    (success: boolean, handoffFailureMessage?: string) => {
      const status: HandoffStatus = success ? 'success' : 'failure';

      dispatch(
        setHandoffStatusAndErrorMessage({
          handoffFailureMessage: handoffFailureMessage,
          handoffStatus: status,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const handleMessage = async (e: MessageEvent) => {
      if (!isEventTrusted(e)) {
        return;
      }

      if (e.data.action === 'openWidget') {
        handleWorkflowTriggerOnClick();

        dispatch(
          sendTrackingEvent({
            event_type: 'widget-opened',
            mechanism: e.data.mechanism,
          }),
        );
      }
      if (e.data.action === 'closeWidget') {
        dispatch(closeWidget());
      }
      if (e.data.action === 'showIframe') {
        // Have to use a non-zero timeout.
        // Otherwise, iframe size will be calculated incorrectly.
        setTimeout(() => {
          postIframeResizeEvent();
        }, 1000);
      }
      if (e.data.action === 'showIframeOpen') {
        scrollToTheBottom('instant');
        // Have to call scrollToTheBottom() AGAIN after a timeout, because the
        // first call doesn't scroll to the very bottom (a few pixels short).
        setTimeout(() => {
          scrollToTheBottom('instant');
        }, 100);
      }
      if (e.data.event === 'handoffStatus') {
        handoffWorkflowsCallback(e.data.success, e.data.handoffFailureMessage);
        handoffSolveCallback(e.data.success, e.data.hideWidget);
      }
      if (e.data.event === 'noZendeskObjectDetected') {
        sendErrorToSentry({
          consoleError: true,
          conversationId: conversationId,
          error: new Error('window.zE is undefined'),
        });
      }
      if (e.data.event === 'triggerEventComplete') {
        const payload = e.data.payload || {};
        const eventName = e.data.identifier || '';
        dispatch(setTriggerEventPayloadContextVariables({ ...payload }));
        dispatch(setTriggerEventPayloadEventName(eventName));
      }
      if (e.data.event === 'updateIntentFiltersForPreview') {
        dispatch(setIntentFiltersForPreview(e.data.intentFiltersForPreview));
        dispatch(setPreviewIsLiveMode(e.data.isLiveMode));
      }
      if (e.data.event === 'handoffTrackingEvent') {
        dispatch(
          sendTrackingEvent({
            ...e.data.handoffData,
            event_type: 'widget-customer-handoff',
          }),
        );
      }
      if (e.data.event === 'zendeskUpdateSettings') {
        dispatch(
          sendTrackingEvent({
            ...e.data.updateSettingsData,
            event_type: 'widget-zendesk-update-settings',
          }),
        );
      }
      if (e.data.action === 'clearLocalData') {
        dispatch(cleanConversation());
        await dispatch(configWidget());
        dispatch(disablePersistence());
      }
      if (e.data.action === 'launchQuery') {
        const initialQuery = e.data.payload?.query;
        if (initialQuery) {
          dispatch(cleanConversation());
          await dispatch(configWidget({ initialQuery }));
          handleWorkflowTriggerOnClick({ initialQueryParam: initialQuery });
          dispatch(
            sendTrackingEvent({
              event_type: 'widget-opened',
              mechanism: 'js-api-widget-launchQuery',
            }),
          );
        }
      }
      if (e.data.event === 'forethoughtSetVisualViewport') {
        dispatch(setVisualViewportOffset(e.data.payload));
      }
      if (e.data.event === 'updateHelpdeskConversation') {
        dispatch(
          updateHelpdeskConversation({
            forethought_conversation_id: e.data.forethoughtConversationID,
            helpdesk_conversation_id: e.data.helpdeskConversationId,
            include_attachments: e.data.includeAttachments,
            include_transcript: e.data.includeTranscript,
            integration: e.data.integration,
            step_type: e.data.stepType,
          }),
        );
      }
      if (e.data.action === 'showProactivePrompt') {
        if (isProactiveChatVisible) {
          return;
        }

        postMessage({ event: 'forethoughtWidgetResizeToMaximumValues' });
        dispatch(showProactivePrompt());
        dispatch(
          sendTrackingEvent({
            event_type: 'widget-customer-proactive-bubbles-shown',
          }),
        );
      }
      if (e.data.action === 'hideProactivePrompt') {
        dispatch(setDidUserCloseProactiveChat(true));
      }
      if (e.data.event === 'forethoughtSendTrackingEvent') {
        dispatch(sendTrackingEvent(e.data.trackingEvent));
      }
      if (e.data.event === 'forethoughtLiveChatSetStepSent') {
        dispatch(setForethoughtLiveChatStepSent());
      }
      if (e.data.event === 'forethoughtSendConversationAlert') {
        dispatch(sendConversationAlert(e.data.requestData));
      }
      if (e.data.event === 'forethoughtClickOutsideWidget') {
        dispatch(handleClickOutsideIframe());
      }
      if (e.data.event === 'updateConversationContext') {
        dispatch(setWindowUpdatedContextVariables(e.data.payload));
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // Method gets updated
  }, [
    conversationId,
    dispatch,
    handleWorkflowTriggerOnClick,
    handoffSolveCallback,
    handoffWorkflowsCallback,
    isProactiveChatVisible,
  ]);
};
